// covered-by: frontends/carrie/src/utils/__tests__/useStartNewProjectChat.test.ts
import { useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";

import { errorAnalyticsPayload, useAnalytics } from "./analytics";
import { PLACEHOLDER_PROJECT_NAME } from "./placeholderProjectName";
import { askPunttKeys } from "./queries/askPuntt";
import {
  askPunttProjectKeys,
  useCreateAskPunttProject,
} from "./queries/askPunttProjects";

/** Default name until the first message with real context retitles the project. */
export const DEFAULT_NEW_PROJECT_NAME = PLACEHOLDER_PROJECT_NAME;

export type StartedAskPunttProject = {
  id: string;
  name: string;
  sharedChatId: string | null;
};

/**
 * Creates a first-class Ask Puntt project (shared chat is seeded server-side
 * with the starter + upload prompt) without a name modal. The caller opens or
 * selects the project so V1 and V2 can route independently. Pass `createFn` to
 * mint on a different URL (V2 stamps agent=warhol on POST /ask-puntt/projects).
 */
export function useStartNewProjectChat(
  analyticsSurface: string,
  openProject: (project: StartedAskPunttProject) => void,
  createFn?: Parameters<typeof useCreateAskPunttProject>[0],
) {
  const queryClient = useQueryClient();
  const createProject = useCreateAskPunttProject(createFn);
  const analytics = useAnalytics(analyticsSurface);
  // why: the name modal used to carry the only double-submit guard
  // (`!createProject.isPending`). A ref blocks the re-entrant call in the same
  // tick, before `isPending` has re-rendered the caller's disabled affordance,
  // so a double-click cannot mint two projects.
  const inFlightRef = useRef(false);

  const startNewProjectChat = async (source: string) => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      const result = await createProject.mutateAsync({
        name: DEFAULT_NEW_PROJECT_NAME,
      });
      const projectId = result.project._id;
      const { sharedChatId, sharedChatBootstrap } = result;
      const started: StartedAskPunttProject = {
        id: projectId,
        name: result.project.name,
        sharedChatId: sharedChatId ?? result.project.sharedChatId ?? null,
      };

      if (sharedChatBootstrap && sharedChatId) {
        queryClient.setQueryData(
          askPunttKeys.messages(sharedChatId),
          sharedChatBootstrap,
        );
      }

      queryClient.invalidateQueries({
        queryKey: askPunttProjectKeys.project(projectId),
      });
      queryClient.invalidateQueries({
        queryKey: askPunttProjectKeys.feed(projectId),
      });
      queryClient.invalidateQueries({
        queryKey: askPunttProjectKeys.timeline(projectId),
      });

      analytics.capture("ask_puntt_project_started_from_launcher", {
        source,
        projectId,
        sharedChatId: started.sharedChatId,
      });
      openProject(started);
    } catch (error) {
      analytics.capture("ask_puntt_project_create_error", {
        source,
        ...errorAnalyticsPayload(error),
      });
      console.error("Failed to start new project:", error);
    } finally {
      inFlightRef.current = false;
    }
  };

  return {
    startNewProjectChat,
    isPending: createProject.isPending,
  };
}
